/**
 * MobileBottomToolbar Component
 *
 * Fixed bottom toolbar for mobile devices across the authenticated app.
 * Provides persistent primary navigation plus quick access to all tools.
 *
 * Features:
 * - Fixed to bottom of screen on mobile only
 * - Compact height (56px) for easy thumb access
 * - Contains "Tools" button that opens AppLauncher dropdown
 * - Expandable for future buttons
 * - Safe area padding for iPhone notches
 * - Z-index above content but below modals
 *
 * Mobile-only: Hidden on desktop (md:hidden)
 * App-wide: Shown on all /app routes
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Wrench,
  Home,
  LayoutDashboard,
  MessageCircle,
  Folder,
  Map,
  Calendar,
  Building2,
  FileText,
  Phone,
  Mail,
  Radio,
  Bot,
  Terminal,
  Database,
  RefreshCw,
  Receipt,
  Kanban,
  Clock,
  Brain,
  BookOpen,
  CalendarDays,
  Upload,
  Inbox,
  SendHorizonal,
  ChevronUp,
  X,
  LucideIcon,
} from 'lucide-react';
import { ROUTES } from '@/app/routes';

// ============================================================================
// Dashboard Card Configuration (same as AppLauncher)
// ============================================================================

interface DashboardCardItem {
  id: string;
  icon: LucideIcon;
  iconColor: string;
  title: string;
  description: string;
  route: string;
}

interface DashboardSection {
  id: string;
  title: string;
  cards: DashboardCardItem[];
}

interface PrimaryNavItem {
  id: string;
  label: string;
  icon: LucideIcon;
  route: string;
  match: (pathname: string) => boolean;
}

/**
 * Get all dashboard sections with their cards.
 * NOTE: Function to avoid circular dependency with ROUTES.
 */
const getDashboardSections = (): DashboardSection[] => [
  {
    id: 'campaign-quick-access',
    title: 'Campaign Quick-Access',
    cards: [
      {
        id: 'event-planning',
        icon: Calendar,
        iconColor: 'purple',
        title: 'Event Planning',
        description: 'Plan and schedule events on the 12-month annual calendar grid',
        route: ROUTES.EPG,
      },
      {
        id: 'organizations',
        icon: Building2,
        iconColor: 'green',
        title: 'Organizations',
        description: 'Browse and manage organizations from your Pipedrive CRM',
        route: ROUTES.ORGANIZATIONS,
      },
    ],
  },
  {
    id: 'post-campaign-execution',
    title: 'Post-Campaign',
    cards: [
      {
        id: 'weekly-drop-report',
        icon: FileText,
        iconColor: 'green',
        title: 'Weekly Drop Report',
        description: 'View dropped organizations grouped by week',
        route: ROUTES.WEEKLY_DROP_REPORT,
      },
      {
        id: 'weekly-call-status',
        icon: Phone,
        iconColor: 'blue',
        title: 'Weekly Call Status',
        description: 'Track call activities by time period',
        route: ROUTES.WEEKLY_CALL_STATUS,
      },
      {
        id: 'follow-ups',
        icon: Mail,
        iconColor: 'orange',
        title: 'Follow-Ups',
        description: 'Manage email follow-ups for your drops',
        route: ROUTES.DROP_FOLLOW_UPS,
      },
    ],
  },
  {
    id: 'administrative',
    title: 'Administrative',
    cards: [
      {
        id: 'ai-assistant',
        icon: MessageCircle,
        iconColor: 'brand',
        title: 'AI Assistant',
        description: 'LibreChat workspace for chat, docs, tools, and artifacts',
        route: ROUTES.ASSISTANT,
      },
      {
        id: 'vibe-remote',
        icon: Radio,
        iconColor: 'brand',
        title: 'Vibe Remote',
        description: 'Remote terminal sessions',
        route: ROUTES.VIBE_REMOTE,
      },
      {
        id: 'cli-dashboard',
        icon: Terminal,
        iconColor: 'gray',
        title: 'CLI Dashboard',
        description: 'Monitor terminal sessions in real-time',
        route: ROUTES.CLI_DASHBOARD,
      },
      {
        id: 'ops-monitor',
        icon: Bot,
        iconColor: 'brand',
        title: 'Ops Monitor',
        description: 'Live commit feed and session status',
        route: ROUTES.CLAWD_BOT,
      },
      {
        id: 'inbox',
        icon: Inbox,
        iconColor: 'blue',
        title: 'Inbox',
        description: 'Outlook inbox monitoring and message triage',
        route: ROUTES.INBOX,
      },
      {
        id: 'field-management',
        icon: Database,
        iconColor: 'cyan',
        title: 'Field Management',
        description: 'Manage Pipedrive field definitions',
        route: ROUTES.FIELD_MANAGEMENT,
      },
      {
        id: 'pug-records',
        icon: RefreshCw,
        iconColor: 'green',
        title: 'PUG Records',
        description: 'View Pipedrive sync records',
        route: ROUTES.PUG_RECORDS,
      },
      {
        id: 'expenses',
        icon: Receipt,
        iconColor: 'orange',
        title: 'Expenses',
        description: 'Upload receipts with OCR',
        route: ROUTES.EXPENSES,
      },
      {
        id: 'project-board',
        icon: Kanban,
        iconColor: 'purple',
        title: 'Project Board',
        description: 'Kanban-style project management',
        route: ROUTES.PLANKA,
      },
    ],
  },
  {
    id: 'client-management',
    title: 'Client Management',
    cards: [
      {
        id: 'client-timelines',
        icon: Calendar,
        iconColor: 'cyan',
        title: 'Client Timelines',
        description: 'Manage enrollment timelines',
        route: ROUTES.TIMELINES,
      },
      {
        id: 'availability',
        icon: Clock,
        iconColor: 'purple',
        title: 'Availability',
        description: 'Find meeting times across teams',
        route: ROUTES.AVAILABILITY,
      },
    ],
  },
  {
    id: 'sales-tools',
    title: 'Sales Tools',
    cards: [
      {
        id: 'flashcards',
        icon: Brain,
        iconColor: 'purple',
        title: 'Flashcards',
        description: 'Study outreach scripts and tips',
        route: ROUTES.FLASHCARDS,
      },
      {
        id: 'notes',
        icon: BookOpen,
        iconColor: 'blue',
        title: 'Notes',
        description: 'Organize notes and documents',
        route: ROUTES.NOTES,
      },
      {
        id: 'houston-calendar',
        icon: CalendarDays,
        iconColor: 'orange',
        title: 'Houston Calendar',
        description: 'Marketing calendar for Houston events',
        route: ROUTES.CALENDAR,
      },
      {
        id: 'hmc-events',
        icon: Upload,
        iconColor: 'cyan',
        title: 'HMC Events',
        description: 'Upload HMC files and view reports',
        route: ROUTES.HMC_EVENTS,
      },
      {
        id: 'file-inbox',
        icon: Inbox,
        iconColor: 'green',
        title: 'File Inbox',
        description: 'Email attachments from Gmail',
        route: ROUTES.FILE_INBOX,
      },
      {
        id: 'usi-events',
        icon: SendHorizonal,
        iconColor: 'purple',
        title: 'USI Events',
        description: 'Send postcard emails to contacts',
        route: ROUTES.USI_EVENTS,
      },
    ],
  },
];

/**
 * Map icon color names to Tailwind CSS classes
 */
const ICON_COLOR_CLASSES: Record<string, string> = {
  purple: 'text-purple-400',
  green: 'text-green-400',
  brand: 'text-brand-500',
  blue: 'text-blue-400',
  orange: 'text-orange-400',
  gray: 'text-dark-muted',
  cyan: 'text-cyan-400',
};

// ============================================================================
// Component Props
// ============================================================================

export interface MobileBottomToolbarProps {
  /** Additional CSS classes */
  className?: string;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Mobile Bottom Toolbar - Fixed bottom navigation with tools button
 *
 * @example
 * ```tsx
 * // In AppShell or DashboardView
 * <MobileBottomToolbar />
 * ```
 */
export const MobileBottomToolbar: React.FC<MobileBottomToolbarProps> = ({
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Dashboard sections for the tools menu
  const dashboardSections = useMemo(() => getDashboardSections(), []);
  const primaryNavItems = useMemo<PrimaryNavItem[]>(() => [
    {
      id: 'home',
      label: 'Home',
      icon: Home,
      route: ROUTES.APP_HOME,
      match: (pathname) => pathname.startsWith(ROUTES.APP_HOME),
    },
    {
      id: 'dashboard',
      label: 'Ops',
      icon: LayoutDashboard,
      route: ROUTES.DASHBOARD,
      match: (pathname) => pathname.startsWith(ROUTES.DASHBOARD),
    },
    {
      id: 'assistant',
      label: 'AI',
      icon: MessageCircle,
      route: ROUTES.ASSISTANT,
      match: (pathname) => pathname.startsWith(ROUTES.ASSISTANT),
    },
    {
      id: 'campaigns',
      label: 'Camps',
      icon: Folder,
      route: ROUTES.CAMPAIGNS,
      match: (pathname) => pathname.startsWith(ROUTES.CAMPAIGNS),
    },
    {
      id: 'routes',
      label: 'Routes',
      icon: Map,
      route: ROUTES.ROUTES,
      match: (pathname) =>
        pathname.startsWith(ROUTES.ROUTES) ||
        pathname.startsWith(ROUTES.REVIEW_ROUTES),
    },
  ], []);

  // ========================================================================
  // Event Handlers
  // ========================================================================

  const handleTileClick = useCallback(
    (route: string) => {
      navigate(route);
      setIsOpen(false);
    },
    [navigate]
  );

  const toggleMenu = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
  }, []);

  // ========================================================================
  // Click Outside / Escape Handler
  // ========================================================================

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  // ========================================================================
  // Render
  // ========================================================================

  return (
    <div
      ref={containerRef}
      className={`
        md:hidden
        fixed bottom-0 left-0 right-0
        z-[55]
        ${className}
      `}
      data-testid="mobile-bottom-toolbar"
    >
      {/* Tools Dropdown Panel - slides up from toolbar */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-[54]"
            onClick={closeMenu}
            aria-hidden="true"
          />

          {/* Panel */}
          <div
            className="
              absolute bottom-full left-0 right-0
              bg-dark-surface border-t border-dark-border/30
              rounded-t-3xl shadow-2xl
              max-h-[74vh] overflow-y-auto
              z-[55]
              animate-slide-up
            "
            role="menu"
            aria-orientation="vertical"
          >
            {/* Panel Header */}
            <div className="sticky top-0 bg-dark-surface px-4 py-3 border-b border-dark-border/30 flex items-center justify-between">
              <div>
                <h2 className="text-sm font-semibold text-dark-text-primary">Tools</h2>
                <p className="text-[11px] text-dark-muted">All workspaces</p>
              </div>
              <button
                onClick={closeMenu}
                className="p-2 rounded-lg hover:bg-dark-surface/50 transition-all duration-150"
                aria-label="Close tools menu"
              >
                <X size={18} className="text-dark-muted" />
              </button>
            </div>

            {/* Dashboard Sections */}
            <div className="py-2">
              {dashboardSections.map((section, sectionIndex) => (
                <div key={section.id}>
                  {/* Section Header */}
                  <div className="px-4 py-2">
                    <span className="text-xs font-medium text-dark-muted uppercase tracking-wider">
                      {section.title}
                    </span>
                  </div>

                  {/* Section Cards - 2 column grid for mobile */}
                  <div className="px-2 pb-2 grid grid-cols-2 gap-1">
                    {section.cards.map((card) => {
                      const Icon = card.icon;
                      const colorClass = ICON_COLOR_CLASSES[card.iconColor] || 'text-brand-500';

                      return (
                        <button
                          key={card.id}
                          type="button"
                          onClick={() => handleTileClick(card.route)}
                          className="
                            flex flex-col items-center gap-1.5
                            px-2 py-3 rounded-lg
                            hover:bg-dark-surface/50 active:bg-dark-surface/60/80
                            transition-all duration-150 text-center
                            min-h-[72px]
                          "
                          role="menuitem"
                          title={card.description}
                        >
                          <div className={`w-10 h-10 flex items-center justify-center rounded-lg bg-dark-bg ${colorClass}`}>
                            <Icon size={20} />
                          </div>
                          <span className="text-xs font-medium text-dark-text-primary line-clamp-1">
                            {card.title}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Section Divider (except for last section) */}
                  {sectionIndex < dashboardSections.length - 1 && (
                    <div className="mx-4 border-b border-dark-border/30" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Bottom Toolbar Bar */}
      <div
        className="
          bg-dark-surface/95 backdrop-blur-sm
          border-t border-dark-border/30
          px-2 py-2
          safe-bottom
        "
      >
        {/* Toolbar Content */}
        <div className="grid grid-cols-6 gap-1 max-w-screen-sm mx-auto">
          {primaryNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = item.match(location.pathname);

            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleTileClick(item.route)}
                className={`
                  flex flex-col items-center justify-center gap-1
                  min-h-[52px] rounded-2xl px-1
                  text-[10px] font-medium sm:text-[11px]
                  transition-all active:scale-95
                  ${isActive
                    ? 'bg-brand-600 text-white shadow-lg shadow-brand-950/30'
                    : 'text-dark-muted hover:bg-dark-surface/60 active:bg-dark-border/60'
                  }
                `}
                aria-current={isActive ? 'page' : undefined}
                data-testid={`mobile-primary-nav-${item.id}`}
              >
                <Icon size={19} />
                <span className="max-w-full truncate">{item.label}</span>
              </button>
            );
          })}

          <button
            type="button"
            onClick={toggleMenu}
            className={`
              relative flex flex-col items-center justify-center gap-1
              min-h-[52px] rounded-2xl px-1
              text-[10px] font-medium sm:text-[11px]
              transition-all
              active:scale-95
              ${isOpen
                ? 'bg-brand-600 text-white shadow-lg shadow-brand-950/30'
                : 'text-dark-muted hover:bg-dark-surface/60 active:bg-dark-border/60'
              }
            `}
            aria-label="Open tools menu"
            aria-expanded={isOpen}
            aria-haspopup="true"
            data-testid="mobile-tools-button"
          >
            <Wrench size={19} />
            <span>Tools</span>
            <ChevronUp
              size={12}
              className={`absolute right-3 top-3 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            />
          </button>
        </div>
      </div>
    </div>
  );
};

export default MobileBottomToolbar;
